import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Send, CheckCircle2 } from "lucide-react";
import { SITE } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch (_) {}
    setDone(true);
    setBusy(false);
    setEmail("");
  };

  return (
    <footer data-testid="site-footer" className="relative text-[#FDF3E3]" style={{ background: "linear-gradient(180deg,#9A2E06 0%,#882414 100%)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <div className="flex justify-center mb-14">
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="w-full max-w-xl flex items-center bg-[#7A1F10] border border-[#B45530] rounded-full p-1.5 pl-5"
          >
            <Send size={16} className="text-[#FCD9AC] shrink-0" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email-input"
              className="flex-1 min-w-0 w-full bg-transparent outline-none px-3 text-base sm:text-sm placeholder-[#E7B98C] text-[#FDF3E3]"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-subscribe-button"
              className="shrink-0 bg-[#FDF3E3] text-[#882414] text-sm font-semibold px-5 sm:px-6 py-2.5 rounded-full hover:bg-white transition-colors"
            >
              {done ? "Blessed ✓" : "Subscribe"}
            </button>
          </form>
        </div>
        {done && (
          <p data-testid="newsletter-success" className="text-center -mt-10 mb-10 text-[#FCD9AC] text-xs tracking-wide">
            You are in the circle. Expect transits, festivals and remedies — never noise.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img src="/brand-logo.webp" alt="Messenger of Shiva" data-testid="footer-logo-image" className="h-14 w-auto object-contain" />
            <p className="mt-5 text-sm leading-relaxed text-[#F3D9BC] max-w-sm">
              Messenger of Shiva is a devoted disciple's house of Vedic astrology — precise kundli readings, energized
              gemstones and karmic remedies for seekers across the world.
            </p>
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="mt-6 inline-flex items-center gap-2.5 bg-white/10 hover:bg-white/20 transition-colors border border-white/25 rounded-full px-5 py-2.5 text-sm font-medium"
            >
              <Instagram size={16} />
              {SITE.handle}
            </a>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-cinzel text-sm tracking-widest uppercase mb-4 text-[#FCD9AC]">Explore</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" data-testid="footer-link-home" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/the-call-consultation" data-testid="footer-link-consultation" className="hover:text-white transition-colors">Consultation</Link></li>
              <li><Link to="/reports" data-testid="footer-link-reports" className="hover:text-white transition-colors">Reports</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-cinzel text-sm tracking-widest uppercase mb-4 text-[#FCD9AC]">Sacred</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/blogs" data-testid="footer-link-blogs" className="hover:text-white transition-colors">Blogs</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-cinzel text-sm tracking-widest uppercase mb-4 text-[#FCD9AC]">Contact</h4>
            <ul className="space-y-2.5 text-sm text-[#F3D9BC]">
              <li>
                <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-contact-instagram" className="hover:text-white transition-colors">
                  Instagram — {SITE.handle}
                </a>
              </li>
              <li>DM for consultations, reports & gemstones</li>
              <li>Guidance offered worldwide, in English & Hindi</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#E7B98C]">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Messenger of Shiva. All rights reserved.</span>
          <span className="font-deva tracking-widest">ॐ नमः शिवाय</span>
        </div>
      </div>
      {done && <CheckCircle2 className="hidden" />}
    </footer>
  );
}
